import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { IconKey, IconLock, IconShield } from '@/components/home/icons';

const CONTACT_EMAIL = 'ehsaasradio@gmail.com';
const LAST_UPDATED = '23 May 2026';

/** Privacy Policy — plain-language, reflecting what the app actually does. */
export function Privacy() {
  useDocumentTitle('Privacy Policy');

  return (
    <section className="mx-auto max-w-3xl px-5 pb-24 pt-12 sm:px-8 sm:pt-16">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-beam">
          Legal
        </p>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-night-50 sm:text-4xl">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-night-300">
          Last updated: {LAST_UPDATED}
        </p>
        <p className="mt-5 leading-relaxed text-night-200">
          This policy explains, in plain language, what Alhudah collects, why,
          and what you can do about it. Alhudah is a Quran reading and study
          app, and it is built to need as little of your data as possible.
        </p>
      </header>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        <Highlight
          icon={<IconShield className="h-5 w-5" />}
          title="Minimal by default"
          body="Reading the Quran needs no account and no personal data at all."
        />
        <Highlight
          icon={<IconLock className="h-5 w-5" />}
          title="Kept secure"
          body="Accounts and saved data are stored with Supabase over encrypted connections."
        />
        <Highlight
          icon={<IconKey className="h-5 w-5" />}
          title="Yours to control"
          body="You can export or delete everything tied to your account at any time."
        />
      </div>

      <div className="mt-12 space-y-10">
        <Section title="What we collect">
          <p>
            <strong className="text-night-100">Without an account:</strong>{' '}
            nothing that identifies you. Reading, searching, and browsing the
            Quran works fully anonymously. Our hosting provider keeps standard,
            short-lived server logs (such as IP address and request time) to
            keep the service running and secure.
          </p>
          <p>
            <strong className="text-night-100">With an account:</strong> your
            email address, used to sign you in, and the data you choose to
            create — your settings, bookmarks, and reading and memorization
            progress.
          </p>
        </Section>

        <Section title="What we don't do">
          <p>
            No advertising. No selling, renting, or sharing your data with
            advertisers or data brokers. No third-party tracking pixels
            following you around the web. We have no interest in building a
            profile of you — only in being a good place to read.
          </p>
        </Section>

        <Section title="AI features">
          <p>
            When you use Ask or Search, the text of your query is sent to our
            AI providers to generate a result — Anthropic for Ask answers, and
            OpenAI to turn a search query into an embedding. Your query is sent
            for that purpose alone. Please do not include sensitive personal
            information in a query.
          </p>
        </Section>

        <Section title="Services we rely on">
          <p>
            Alhudah is built on a few trusted providers, each handling only its
            own part of the service:
          </p>
          <ul className="mt-3 space-y-2">
            <Provider name="Supabase" role="Database and account authentication." />
            <Provider name="Anthropic" role="Generates AI answers in Ask." />
            <Provider name="OpenAI" role="Creates embeddings for semantic search." />
            <Provider name="Cloudflare" role="Hosting and content delivery." />
          </ul>
        </Section>

        <Section title="Your rights">
          <p>You are in control of any data tied to your account:</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Right title="Access" body="Ask for a copy of the data we hold for you." />
            <Right title="Correction" body="Have anything inaccurate put right." />
            <Right title="Deletion" body="Delete your account and its data entirely." />
            <Right title="Portability" body="Receive your data in a portable format." />
          </div>
          <p className="mt-4">
            To exercise any of these, email{' '}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-beam hover:underline"
            >
              {CONTACT_EMAIL}
            </a>{' '}
            and we will help.
          </p>
        </Section>
      </div>

      <div className="mt-12 rounded-2xl border border-night-500/40 bg-night-800/60 p-7">
        <h2 className="font-display text-lg font-bold text-night-50">
          Changes to this policy
        </h2>
        <p className="mt-2 leading-relaxed text-night-200">
          If our practices change, we will update this page and revise the date
          at the top. For anything unclear, reach us through the{' '}
          <Link to="/contact" className="text-beam hover:underline">
            contact page
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

function Highlight({
  icon,
  title,
  body,
}: {
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border border-night-500/40 bg-night-700/60 p-5">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-beam/30 bg-beam/10 text-beam">
        {icon}
      </span>
      <h3 className="mt-3 font-display text-sm font-bold text-night-50">
        {title}
      </h3>
      <p className="mt-1 text-xs leading-relaxed text-night-300">{body}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-xl font-bold text-night-50">{title}</h2>
      <div className="mt-3 space-y-3 leading-relaxed text-night-200">
        {children}
      </div>
    </section>
  );
}

function Provider({ name, role }: { name: string; role: string }) {
  return (
    <li className="flex gap-2.5">
      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-beam" />
      <span>
        <strong className="text-night-100">{name}</strong> — {role}
      </span>
    </li>
  );
}

function Right({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-night-500/40 bg-night-700/50 p-4">
      <h3 className="font-display text-sm font-bold text-night-50">{title}</h3>
      <p className="mt-1 text-xs leading-relaxed text-night-300">{body}</p>
    </div>
  );
}
